import React from "react";
import i18n from "@microdoing/shared/i18n";
import { changeComment } from "@microdoing/shared/services/action/actions";
import { getComment } from "@microdoing/shared/services/action/selectors";
import { getFilesToUpload } from "@microdoing/shared/services/upload/selectors";
import {
  getDebriefText,
  getStepSelected,
} from "@microdoing/shared/services/userCampaign/selectors";
import { Box, MainButton, MainText } from "app/components/common";
import KeyboardAvoidWrapper from "app/components/common/KeyboardAvoidWrapper";
import theme from "app/Theme/Theme";
import normalize from "app/utils/normalize";
import { Keyboard, TextInput, View } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { useLaunchCamera } from "../../useLaunchCamera";
import { UploadFilesScrollView } from "../Component/UploadFilesScrollView";
import ImageIcon from "app/components/Icons/ImageIcon";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import selectLanguage from "@microdoing/shared/utils/selectLanguage";
import { getLanguage } from "@microdoing/shared/services/profileSettings/selectors";

interface ITextAndImageAnalysis {
  setIsEditingComment: (isEditing: boolean) => void;
  isEditingComment: boolean;
}

const TextAndImageAnalysis = ({
  setIsEditingComment,
  isEditingComment,
}: ITextAndImageAnalysis) => {
  const comment = useSelector(getComment);
  const filesToUpload = useSelector(getFilesToUpload);
  const stepSelected = useSelector(getStepSelected);
  const debriefText = useSelector(getDebriefText);
  const language = useSelector(getLanguage);

  const {
    settings = {
      uploadInstruction: {},
      uploadOptions: ["video", "photo", "document"],
      isUploadRequired: false,
    },
  } = stepSelected;

  const {
    uploadOptions = ["video", "photo", "document"],
    isUploadRequired = false,
  } = settings;

  const hasAllOptions = uploadOptions.length === 0 && isUploadRequired;

  const hasVideoOption =
    !!uploadOptions?.find(el => el === "video") || hasAllOptions;
  const hasPhotoOption =
    !!uploadOptions?.find(el => el === "photo" || el === "picture") ||
    hasAllOptions;

  const dispatch = useDispatch();

  const [error, setError, openNativeModule] = useLaunchCamera({
    hasPhotoOption,
    hasVideoOption: false,
  });

  const handleEndEditing = () => {
    Keyboard.dismiss();
    setIsEditingComment(false);
  };

  const focusCommentArea = e => {
    e?.stopPropagation();
    setIsEditingComment(true);
  };

  const onChangeComment = text => {
    dispatch(changeComment(text));
  };

  return (
    <KeyboardAvoidWrapper>
      <View
        style={{
          paddingBottom: isWebDesktop ? 0 : normalize(130),
          height: "100%",
        }}
      >
        <Box marginBottom="sl" width="100%">
          <MainText variant="l4-medium" color="neutral.600">
            {selectLanguage({
              text: settings?.uploadInstruction,
              language,
            }) ||
              selectLanguage({ text: debriefText, language }) ||
              i18n.t("feeling-commentary-desc")}
          </MainText>
        </Box>
        <Box
          width="100%"
          borderRadius={normalize(6)}
          marginBottom="xxs"
          borderWidth={isEditingComment ? 2 : 1}
          borderColor={
            isEditingComment ? "brand.500" : "neutral.transparent.white.80"
          }
        >
          <TextInput
            hitSlop={{
              top: normalize(10),
              bottom: normalize(30),
              left: normalize(10),
              right: normalize(20),
            }}
            maxFontSizeMultiplier={1.2}
            selectionColor={theme.colors["brand.500"]}
            onFocus={focusCommentArea}
            onEndEditing={handleEndEditing}
            placeholder={i18n.t("comment-placeholder")}
            multiline
            onChangeText={onChangeComment}
            style={{
              ...theme.body["l4-medium"],
              zIndex: 10,
              outlineWidth: 0,
              margin: normalize(20),
              marginRight: normalize(35),
              height: normalize(90),
              padding: normalize(0),
              paddingTop: normalize(4),
              textAlignVertical: "top",
            }}
            placeholderTextColor={theme.colors["neutral.500"]}
            autoFocus={true}
            value={comment}
            enablesReturnKeyAutomatically
            underlineColorAndroid="transparent"
          />
        </Box>

        <MainButton
          color="neutral.white"
          onPress={openNativeModule("gallery")}
          rightIcon={<ImageIcon color={theme.colors["brand.500"]} />}
          textVariant="l4-bold"
          textMargin={2}
          text={i18n.t("add-picture")}
          textColor="neutral.900"
          height={60}
          borderWidth={1}
          shadow
        />
        {filesToUpload?.length === 0 && (
          <Box
            width="100%"
            alignSelf="flex-start"
            justifyContent={filesToUpload?.length ? "flex-start" : "center"}
            alignItems="center"
            flexDirection="row"
            marginTop="xs"
          >
            <MainText
              color={isUploadRequired ? "system.red.500" : "neutral.600"}
              variant="l3-medium"
              textAlign="center"
            >
              {i18n.t("no-files-to-upload")}
            </MainText>
          </Box>
        )}
        {filesToUpload?.length > 0 && (
          <Box
            width="100%"
            alignSelf="flex-start"
            justifyContent={filesToUpload?.length ? "flex-start" : "center"}
            alignItems="center"
            flexDirection="row"
            overflow="visible"
          >
            <UploadFilesScrollView isComingFromReflexiveAnalysis />
          </Box>
        )}
      </View>
    </KeyboardAvoidWrapper>
  );
};

export default TextAndImageAnalysis;
